import { Link } from 'react-router-dom';

import { type NavItem } from '@/lib/layout.config';
import { SidebarNavBadge } from '@/shared/components/Layout/SidebarNavBadge';
import {
  SIDEBAR_NAV_ROW_ACTIVE_CLASSES,
  SIDEBAR_NAV_ROW_CLASSES,
  SIDEBAR_NAV_ROW_IDLE_CLASSES,
} from '@/shared/components/Layout/SidebarNav/sidebar-nav.tokens';
import { cn } from '@/shared/utils/cn/cn.util';

export interface SidebarNavLinkProps {
  item: NavItem;
  /** Already translated — the gate that decided to render this row did that. */
  label: string;
  /**
   * Decided by the parent, not by the link: a `NavLink` matches by prefix and
   * would light `/payments` beside `/payments/events`. The parent knows the
   * siblings and picks one row (`resolveActiveNavPath`).
   */
  isActive: boolean;
  /** Collapsed rail: icon only, the label carried by the accessible name. */
  isCollapsed: boolean;
}

/** A row that IS a page. */
export function SidebarNavLink({ item, label, isActive, isCollapsed }: SidebarNavLinkProps) {
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      // The collapsed rail shows no text, so the accessible name has to come
      // from somewhere — `title` also gives a native tooltip on hover.
      title={isCollapsed ? label : undefined}
      className={cn(
        SIDEBAR_NAV_ROW_CLASSES,
        isActive ? SIDEBAR_NAV_ROW_ACTIVE_CLASSES : SIDEBAR_NAV_ROW_IDLE_CLASSES,
        isCollapsed && 'justify-center px-0',
      )}
    >
      <Icon aria-hidden className="h-4 w-4 shrink-0" />
      {isCollapsed ? (
        <span className="sr-only">{label}</span>
      ) : (
        <span className="truncate">{label}</span>
      )}
      {/* Rendered only when the item declares a count, so the hook inside is
          unconditional (F3). The rail has no room for it; the label carries
          the count in its accessible name there too, through the same hook. */}
      {item.useBadge === undefined || isCollapsed ? null : (
        <SidebarNavBadge useCount={item.useBadge} label={label} />
      )}
    </Link>
  );
}
